import { useEffect, useRef } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { writeSessionStorage } from '@/hooks/useSessionStorage';
import { useSelector } from '@/redux/helper';
import { clearLegacyGuidedTour } from '@/redux/slices/guidedTour/actions';
import { seedTourState } from '@/redux/slices/tours/actions';

import { planLegacyGuidedTourMigration } from '../persistence/legacyMigration';
import { GUIDED_JOURNEY_ID } from '../registry';
import { MOBILE_WARNING_DISMISSED_KEY } from './ui/MobileWarningModal';

/**
 * Folds rehydrated legacy `guidedTour` progress into `tours` once the session user is known.
 * Mounted globally (outside the Router). The legacy slice is migration-only after this runs.
 *
 * TODO(sc-6875): Remove after one release once legacy `guidedTour` persist keys are gone —
 * delete this component, `persistence/legacyMigration.ts`, the `guidedTour` redux slice /
 * persist config, and the `<TourLegacyMigration />` mount in `src/index.js`.
 */
const TourLegacyMigration = () => {
  const dispatch = useDispatch();
  const sessionChecked = useSelector((state) => state.user.sessionChecked);
  const logged = useSelector((state) => state.user.logged);
  const userId = useSelector((state) => state.user.userId);
  const legacy = useSelector((state) => state.guidedTour);
  const existingGuidedJourney = useSelector((state) => state.tours.tours[GUIDED_JOURNEY_ID]);

  // One pass per logged-in userId. Logout clears tours; a different user must be considered again.
  const migratedForUserRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!sessionChecked || !logged || !userId) {
      if (!logged) migratedForUserRef.current = undefined;
      return;
    }
    if (migratedForUserRef.current === userId) return;
    migratedForUserRef.current = userId;

    const plan = planLegacyGuidedTourMigration({
      legacy,
      existingGuidedJourney,
      currentUserId: userId,
    });

    if (plan.mobileWarningDismissed) {
      // writeSessionStorage (not setItem) so a mounted MobileWarningModal re-renders and hides.
      writeSessionStorage(MOBILE_WARNING_DISMISSED_KEY, true);
    }
    if (plan.tourState) {
      dispatch(seedTourState({ tourId: GUIDED_JOURNEY_ID, tourState: plan.tourState }));
    }
    if (plan.shouldClearLegacy) {
      dispatch(clearLegacyGuidedTour());
    }
  }, [sessionChecked, logged, userId, legacy, existingGuidedJourney, dispatch]);

  return null;
};

export default TourLegacyMigration;
